import { Plus, Trash2 } from "lucide-react";

export const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition-[border-color,box-shadow] duration-200 focus:border-aqua-600 focus:ring-4 focus:ring-aqua-600/10";

const cloneEmpty = (v) => {
  if (typeof v === "string") return "";
  if (Array.isArray(v)) return [];
  if (typeof v === "object" && v !== null) {
    return Object.fromEntries(Object.entries(v).map(([k, val]) => [k, cloneEmpty(val)]));
  }
  return v;
};

const getAtPath = (obj, path) => path.reduce((o, k) => o?.[k], obj);

const setAtPath = (obj, path, value) => {
  const [k, ...rest] = path;
  const copy = Array.isArray(obj) ? [...obj] : { ...obj };
  copy[k] = rest.length === 0 ? value : setAtPath(copy[k], rest, value);
  return copy;
};

const removeAtPath = (obj, path) => {
  const index = path[path.length - 1];
  const parentPath = path.slice(0, -1);
  const parent = getAtPath(obj, parentPath);
  return setAtPath(obj, parentPath, parent.filter((_, j) => j !== index));
};

function flattenFields(value, path = [], label = "", depth = 0) {
  const nodes = [];
  if (typeof value === "string" || typeof value === "boolean") {
    nodes.push({
      kind: "field", path, label, depth, value,
      inputType: typeof value === "boolean" ? "boolean" : value.length > 90 ? "textarea" : "text",
    });
  } else if (Array.isArray(value)) {
    nodes.push({
      kind: "arrayHeader", path, label, depth, count: value.length,
      template: value.length ? cloneEmpty(value[value.length - 1]) : "",
    });
    value.forEach((item, i) => {
      if (typeof item === "string") {
        nodes.push({ kind: "field", path: [...path, i], label: `${i + 1}`, depth: depth + 1, value: item, inputType: "text", removable: true });
      } else {
        nodes.push({ kind: "itemHeader", path: [...path, i], label: `${label} · ${i + 1}`, depth: depth + 1 });
        Object.entries(item).forEach(([k, v]) => {
          nodes.push(...flattenFields(v, [...path, i, k], k, depth + 2));
        });
      }
    });
  } else if (typeof value === "object" && value !== null) {
    if (label) nodes.push({ kind: "groupLabel", label, depth });
    Object.entries(value).forEach(([k, v]) => {
      nodes.push(...flattenFields(v, [...path, k], k, depth + 1));
    });
  }
  return nodes;
}

function NodeRow({ node, root, onChange }) {
  const pad = { marginLeft: `${Math.min(node.depth, 5) * 14}px` };
  const update = (v) => onChange(setAtPath(root, node.path, v));
  const remove = () => onChange(removeAtPath(root, node.path));

  if (node.kind === "groupLabel") {
    return (
      <div style={pad} className="border-b border-slate-100 pb-1 pt-3 text-[11px] font-extrabold uppercase tracking-[0.15em] text-slate-400">
        {node.label}
      </div>
    );
  }

  if (node.kind === "arrayHeader") {
    return (
      <div style={pad} className="flex items-center justify-between border-b border-slate-100 pb-1 pt-3">
        <span className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-slate-400">
          {node.label} ({node.count})
        </span>
        <button
          data-testid={`admin-add-${node.path.join("-")}`}
          onClick={() => onChange(setAtPath(root, node.path, [...getAtPath(root, node.path), node.template]))}
          className="flex items-center gap-1 rounded-full border border-navy/20 px-3 py-1 text-[11px] font-bold text-navy transition-colors hover:bg-navy hover:text-white"
        >
          <Plus size={11} /> Add
        </button>
      </div>
    );
  }

  if (node.kind === "itemHeader") {
    return (
      <div style={pad} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
        <span className="text-xs font-bold text-navy">{node.label}</span>
        <button
          data-testid={`admin-remove-${node.path.join("-")}`}
          onClick={remove}
          className="flex h-6 w-6 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-maple/10 hover:text-maple"
          aria-label="Remove item"
        >
          <Trash2 size={12} />
        </button>
      </div>
    );
  }

  if (node.inputType === "boolean") {
    return (
      <label style={pad} className="flex items-center gap-2.5">
        <input type="checkbox" checked={node.value} onChange={(e) => update(e.target.checked)} className="h-4 w-4 accent-[#D93A2B]" data-testid={`admin-field-${node.path.join("-")}`} />
        <span className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{node.label}</span>
      </label>
    );
  }

  return (
    <div style={pad} className="flex items-start gap-2">
      <label className="block flex-1">
        <span className="mb-1 block text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{node.label}</span>
        {node.inputType === "textarea" ? (
          <textarea rows={3} value={node.value} onChange={(e) => update(e.target.value)} className={`${inputCls} resize-y`} data-testid={`admin-field-${node.path.join("-")}`} />
        ) : (
          <input value={node.value} onChange={(e) => update(e.target.value)} className={inputCls} data-testid={`admin-field-${node.path.join("-")}`} />
        )}
      </label>
      {node.removable && (
        <button
          data-testid={`admin-remove-${node.path.join("-")}`}
          onClick={remove}
          className="mt-6 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-maple/10 hover:text-maple"
          aria-label="Remove"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

export function SectionEditor({ value, onChange }) {
  const nodes = flattenFields(value);
  return (
    <div className="space-y-3">
      {nodes.map((n, i) => (
        <NodeRow key={i} node={n} root={value} onChange={onChange} />
      ))}
    </div>
  );
}
